import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, IconButton, Input, InputGroup, InputLeftElement,
    Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, SimpleGrid, Skeleton, Text,
    Tooltip, VStack, useBreakpointValue, useToast,
} from '@chakra-ui/react';
import { FaEdit, FaMapMarkerAlt, FaPhone, FaPlus, FaSearch, FaStore, FaToggleOff, FaToggleOn } from 'react-icons/fa';
import ConfirmDialog from '../ConfirmDialog';
import FormEnderecoColeta from './FormEnderecoColeta';
import { formatarTelefone } from '../ModalSolicitacaoLalamove/telefone';
import { alternarPontoColetaLalamove, getPontosColetaLalamove } from '../../services/api';

const data = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : null);

/**
 * Enderecos de coleta dos fornecedores (#2307): de onde a Lalamove busca a
 * compra. Desativar em vez de apagar, para nao perder a historia das corridas.
 */
export default function AbaPontosColeta({ ativa }) {
    const toast = useToast();
    const compacto = useBreakpointValue({ base: true, md: false });

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [dados, setDados] = useState(null);
    const [busca, setBusca] = useState('');
    const [editando, setEditando] = useState(undefined); // undefined fechado, null novo, objeto editar
    const [alternando, setAlternando] = useState(null);
    const [processando, setProcessando] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            setDados(await getPontosColetaLalamove());
        } catch (e) {
            setErro(e.response?.data?.error || 'Não foi possível carregar os endereços de coleta.');
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { if (ativa && !dados) carregar(); }, [ativa, dados, carregar]);

    const confirmarAlternar = async () => {
        if (!alternando) return;
        setProcessando(true);
        try {
            const res = await alternarPontoColetaLalamove(alternando.id, !alternando.ativo);
            toast({
                title: res.ponto?.ativo ? 'Endereço reativado' : 'Endereço desativado',
                description: res.ponto?.ativo
                    ? `${res.ponto.nome} volta a aparecer na hora de pedir coleta.`
                    : `${res.ponto.nome} não aparece mais para novas coletas. Dá para reativar a qualquer momento.`,
                status: 'success',
                isClosable: true,
            });
            setAlternando(null);
            carregar();
        } catch (e) {
            toast({ title: 'Não foi possível alterar', description: e.response?.data?.error, status: 'error', isClosable: true });
        } finally {
            setProcessando(false);
        }
    };

    const termo = busca.trim().toLowerCase();
    const pontos = (dados?.pontos || []).filter((p) => !termo
        || [p.nome, p.fornecedor_nome, p.cotacao_fornecedor_nome, p.endereco?.linha].some((v) => String(v || '').toLowerCase().includes(termo)));

    const semEndereco = [
        ...(dados?.fornecedores?.listas || []).filter((f) => !f.ponto_coleta_id).map((f) => f.nome),
    ];

    return (
        <VStack align="stretch" spacing={4}>
            <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Onde a Lalamove busca as compras</Text>
                    <Text fontSize="xs" color="gray.500">
                        Usado nas coletas pedidas da Cobertura de Furos, do Kanban de Compras e da corrida avulsa.
                    </Text>
                </Box>
                <HStack spacing={2} w={{ base: '100%', md: 'auto' }}>
                    <InputGroup size="sm" maxW={{ base: '100%', md: '220px' }}>
                        <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" boxSize={3} /></InputLeftElement>
                        <Input borderRadius="lg" placeholder="Buscar fornecedor" value={busca} onChange={(e) => setBusca(e.target.value)} />
                    </InputGroup>
                    <Button size="sm" colorScheme="purple" leftIcon={<Icon as={FaPlus} boxSize={3} />} onClick={() => setEditando(null)} flexShrink={0}>
                        Novo endereço
                    </Button>
                </HStack>
            </Flex>

            {!carregando && semEndereco.length > 0 && (
                <Alert status="info" borderRadius="lg" fontSize="sm">
                    <AlertIcon />
                    Ainda sem endereço de coleta: {semEndereco.join(', ')}.
                </Alert>
            )}

            {erro && <Alert status="error" borderRadius="lg" fontSize="sm"><AlertIcon />{erro}</Alert>}

            {carregando ? (
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
                    {[...Array(3)].map((_, i) => <Skeleton key={i} height="150px" borderRadius="xl" />)}
                </SimpleGrid>
            ) : pontos.length === 0 ? (
                <VStack py={10} spacing={2}>
                    <Icon as={FaMapMarkerAlt} boxSize={8} color="gray.300" />
                    <Text color="gray.500" fontSize="sm">
                        {termo ? 'Nenhum endereço encontrado.' : 'Nenhum endereço de coleta cadastrado.'}
                    </Text>
                    {!termo && (
                        <Button size="sm" variant="outline" colorScheme="purple" onClick={() => setEditando(null)}>
                            Cadastrar o primeiro
                        </Button>
                    )}
                </VStack>
            ) : (
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
                    {pontos.map((p) => (
                        <Box key={p.id} bg="white" borderWidth="1px" borderColor={p.ativo ? 'gray.100' : 'gray.200'}
                            borderRadius="xl" p={4} shadow="sm" opacity={p.ativo ? 1 : 0.7} transition="all 0.15s"
                            _hover={{ shadow: 'md', borderColor: 'purple.100' }}>
                            <Flex justify="space-between" align="start" gap={2} mb={2}>
                                <HStack spacing={2} minW={0}>
                                    <Icon as={FaStore} color={p.ativo ? 'purple.400' : 'gray.400'} boxSize={4} flexShrink={0} />
                                    <Text fontWeight="bold" fontSize="sm" noOfLines={1}>{p.nome}</Text>
                                    {!p.ativo && <Badge colorScheme="gray" fontSize="9px">desativado</Badge>}
                                </HStack>
                                <HStack spacing={0} flexShrink={0}>
                                    <Tooltip label="Editar" hasArrow>
                                        <IconButton size="sm" variant="ghost" aria-label={`Editar ${p.nome}`} icon={<Icon as={FaEdit} />} onClick={() => setEditando(p)} />
                                    </Tooltip>
                                    <Tooltip label={p.ativo ? 'Desativar' : 'Reativar'} hasArrow>
                                        <IconButton size="sm" variant="ghost" colorScheme={p.ativo ? 'gray' : 'green'}
                                            aria-label={p.ativo ? `Desativar ${p.nome}` : `Reativar ${p.nome}`}
                                            icon={<Icon as={p.ativo ? FaToggleOn : FaToggleOff} boxSize={4} />}
                                            onClick={() => setAlternando(p)} />
                                    </Tooltip>
                                </HStack>
                            </Flex>

                            <Text fontSize="xs" color="gray.600" noOfLines={2} mb={2}>{p.endereco?.linha || 'sem endereço'}</Text>

                            <HStack spacing={1} wrap="wrap" mb={2}>
                                {p.fornecedor_nome && <Badge colorScheme="teal" variant="subtle" fontSize="9px">Listas: {p.fornecedor_nome}</Badge>}
                                {p.cotacao_fornecedor_nome && <Badge colorScheme="orange" variant="subtle" fontSize="9px">Furos: {p.cotacao_fornecedor_nome}</Badge>}
                            </HStack>

                            <HStack spacing={3} fontSize="xs" color="gray.500" wrap="wrap">
                                <HStack spacing={1}>
                                    <Icon as={FaPhone} boxSize={2.5} />
                                    <Text>{p.contato_nome ? `${p.contato_nome} · ` : ''}{formatarTelefone(p.contato_telefone) || 'sem telefone'}</Text>
                                </HStack>
                                <Text>{p.corridas} corrida(s){p.ultima_corrida ? `, última em ${data(p.ultima_corrida)}` : ''}</Text>
                            </HStack>
                            {p.instrucoes && <Text fontSize="xs" color="gray.500" mt={2} noOfLines={2}>“{p.instrucoes}”</Text>}
                        </Box>
                    ))}
                </SimpleGrid>
            )}

            <Modal isOpen={editando !== undefined} onClose={() => setEditando(undefined)} size={compacto ? 'full' : '3xl'} scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent borderRadius={compacto ? 0 : 'xl'}>
                    <ModalHeader fontSize="md" borderBottomWidth="1px">
                        {editando ? `Endereço de coleta · ${editando.nome}` : 'Novo endereço de coleta'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody py={4}>
                        {editando !== undefined && (
                            <FormEnderecoColeta
                                ponto={editando}
                                fornecedores={dados?.fornecedores}
                                compacto={compacto}
                                onCancelar={() => setEditando(undefined)}
                                onSalvo={() => { setEditando(undefined); carregar(); }}
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <ConfirmDialog
                isOpen={!!alternando}
                onClose={() => setAlternando(null)}
                onConfirm={confirmarAlternar}
                isLoading={processando}
                title={alternando?.ativo ? `Desativar o endereço de ${alternando?.nome}?` : `Reativar o endereço de ${alternando?.nome}?`}
                body={alternando?.ativo
                    ? 'Ele deixa de aparecer na hora de pedir coleta. As corridas que já usaram este endereço continuam no histórico, e dá para reativar depois.'
                    : 'Ele volta a aparecer na hora de pedir coleta deste fornecedor.'}
                confirmText={alternando?.ativo ? 'Desativar' : 'Reativar'}
                confirmColor={alternando?.ativo ? 'red' : 'green'}
            />
        </VStack>
    );
}
